import axios from 'axios'
import NewsCard from '../components/NewsCard'
import NewsCardReferenz from '../components/NewsCardReferenz'

interface FilterSelections {
  [key: string]: Set<string>
}

class NewsFilter {
  private grid: HTMLElement | null
  private noNewsElement: HTMLElement | null
  private filterSelections: FilterSelections = {}
  private initialData: any[] | null = null
  private isInitialLoad: boolean = true
  private loadMoreButton: HTMLElement | null

  constructor() {
    this.grid = document.querySelector('.news-masonry .row')
    this.noNewsElement = document.querySelector('.no-news-found')
    this.loadMoreButton = document.getElementById('loadMoreNews')
    this.initializeFilters()
    this.initializeLoadMore()
    this.initialize()
  }

  private async initialize(): Promise<void> {
    const urlParams = new URLSearchParams(window.location.search)
    const filterParam = urlParams.get('filter')

    if (filterParam) {
      await this.checkUrlParamsAndApplyFilters()
    }
  }

  private async fetchInitialData(): Promise<void> {
    try {
      const response = await this.getAllNews()
      this.initialData = response.data
      await this.processResponse(response.data)
    } catch (error) {
      console.error('Error fetching initial data:', error)
    }
  }

  private async getAllNews() {
    // @ts-ignore - defined in template
    const splittedUrl = actionUrl.split(';')
    // @ts-ignore - defined in template
    const url = `${splittedUrl[0]}${splittedUrl[1]}&tx_ytdcg_news[categoryUid]=${currentCategory}&${splittedUrl[2]}${splittedUrl[3]}`
    return await axios.get(url)
  }

  private initializeFilters(): void {
    document.querySelectorAll('.multiselect-dropdown').forEach((dropdown) => {
      const category = dropdown.getAttribute('data-filter-category')
      if (category) {
        this.filterSelections[category] = new Set<string>()
      }
    })

    const filterButton = document.getElementById('filter-news')
    filterButton?.addEventListener('click', () => this.applyFilters())

    const resetButton = document.getElementById('resetFilter')
    resetButton?.addEventListener('click', async () => {
      await this.resetFilters()
      const hideAnimation = this.hideExistingNews()
      await this.fetchAndShowAllNews(hideAnimation)
    })
  }

  private initializeLoadMore(): void {
    this.loadMoreButton?.addEventListener('click', (e) => {
      e.preventDefault()
      this.handleLoadMore()
    })
  }

  private async handleLoadMore(): Promise<void> {
    if (!this.loadMoreButton) return

    this.loadMoreButton.classList.add('loading')

    // First we check via pagination if there are more news
    const allPaginatorLinks = document.querySelectorAll(
      '.f3-widget-paginator li:not(.next) a'
    )

    if (allPaginatorLinks.length > 0) {
      try {
        const response = await fetch(
          allPaginatorLinks[0].getAttribute('href') as RequestInfo
        )
        const html = await response.text()

        const parser = new DOMParser()
        const temp = parser.parseFromString(html, 'text/html')
        const moreNewsCol = temp.querySelectorAll('.news-col')
        const newsRow = document.querySelector('.news-row')
        const paginator = document.querySelector('.f3-widget-paginator')

        if (newsRow && paginator) {
          moreNewsCol.forEach((news, index) => {
            newsRow.insertBefore(news, paginator)
            setTimeout(() => {
              setTimeout(() => {
                news.classList.toggle('show')
              }, 50 * index)
            }, 500)
          })
        }

        // Remove the pagination link we just used
        allPaginatorLinks[0].remove()

        // Hide the load more button if no more pagination links
        if (
          document.querySelectorAll('.f3-widget-paginator li:not(.next) a')
            .length === 0
        ) {
          this.loadMoreButton.classList.add('d-none')
        }
      } catch (error) {
        console.error('Error loading more news:', error)
      } finally {
        this.loadMoreButton.classList.remove('loading')
      }
    }
  }

  private hideExistingNews(): number {
    const allNewsCol = document.querySelectorAll('.news-col')
    allNewsCol.forEach((news, index) => {
      setTimeout(() => {
        news.classList.add('hide')
      }, 50 * index)
    })

    const animationDuration = 50 * allNewsCol.length + 1000
    setTimeout(() => {
      allNewsCol.forEach((news) => news.remove())
    }, animationDuration)

    return animationDuration
  }

  private async fetchAndShowAllNews(delay: number = 0): Promise<void> {
    try {
      const response = await this.getAllNews()
      // Wait for hide animation to complete
      await new Promise((resolve) => setTimeout(resolve, delay))
      await this.processResponse(response.data)
    } catch (error) {
      console.error('Error fetching all news:', error)
    }
  }

  private async applyFilters(): Promise<void> {
    const allNewsCol = document.querySelectorAll('.news-col')

    // Only animate if not initial load
    if (!this.isInitialLoad) {
      allNewsCol.forEach((news, index) => {
        setTimeout(() => {
          news.classList.add('hide')
        }, 50 * index)
      })

      setTimeout(() => {
        allNewsCol.forEach((news) => news.remove())
      }, 50 * allNewsCol.length + 1000)
    } else {
      allNewsCol.forEach((news) => news.remove())
    }

    const activeFilters = this.getAllActiveFilters()

    // Always update URL, regardless of initial load
    this.updateURL(activeFilters)

    try {
      const callURL = this.buildApiUrl(activeFilters)
      const response = await axios.get(callURL)
      await this.processResponse(response.data)

      // Reset the visibility of the load more button
      this.resetLoadMoreButton()
    } catch (error) {
      console.error('Error fetching filtered news:', error)
    } finally {
      this.isInitialLoad = false
    }
  }

  private resetLoadMoreButton(): void {
    // Show the load more button if there are pagination links
    const hasPaginationLinks =
      document.querySelectorAll('.f3-widget-paginator li:not(.next) a').length >
      0

    if (this.loadMoreButton) {
      if (hasPaginationLinks) {
        this.loadMoreButton.classList.remove('d-none')
      } else {
        this.loadMoreButton.classList.add('d-none')
      }
    }
  }

  private getAllActiveFilters(): string[] {
    const activeFilters: string[] = []
    document.querySelectorAll('.multiselect-dropdown').forEach((dropdown) => {
      const checkboxes = dropdown.querySelectorAll(
        'input[type="checkbox"]:checked'
      )
      checkboxes.forEach((checkbox) => {
        if (checkbox instanceof HTMLInputElement) {
          activeFilters.push(checkbox.value)
        }
      })
    })
    return activeFilters
  }

  private updateURL(activeFilters: string[]): void {
    const url = new URL(window.location.href)
    const filterParams = this.getUrlParameters(activeFilters)
    if (filterParams.length > 0) {
      url.searchParams.set('filter', filterParams.join(','))
    } else {
      url.searchParams.delete('filter')
    }
    window.history.pushState({}, '', url)
  }

  private getUrlParameters(activeFilters: string[]): string[] {
    const params: string[] = []
    document
      .querySelectorAll('input[type="checkbox"]:checked')
      .forEach((checkbox) => {
        if (checkbox instanceof HTMLInputElement) {
          const urlParam = checkbox.getAttribute('data-url-parameter')
          if (urlParam) params.push(urlParam)
        }
      })
    return params
  }

  private buildApiUrl(activeFilters: string[]): string {
    // @ts-ignore - defined in template
    const splittedUrl = actionUrl.split(';')
    return `${splittedUrl[0]}${
      splittedUrl[1]
    }&tx_ytdcg_news[tagUid]=${activeFilters.join(
      ','
    )}&tx_ytdcg_news[categoryUid]=${
      // @ts-ignore - defined in template
      currentCategory
    }&${splittedUrl[2]}${splittedUrl[3]}`
  }

  private async processResponse(data: any[]): Promise<void> {
    if (!this.grid) return

    this.grid.innerHTML = ''

    if (data.length > 0) {
      this.noNewsElement?.classList.add('d-none')

      data.forEach((item) => {
        if (item.tags) {
          if (item.categoryUid == 8) {
            this.grid?.append(
              new NewsCardReferenz(
                item.categories,
                item.image,
                item.link,
                item.title
              )
            )
          } else {
            this.grid?.append(
              new NewsCard(
                item.categories,
                item.image,
                item.link,
                item.tags,
                item.teaser,
                item.title
              )
            )
          }
        }
      })

      this.arrangeCards()
    } else {
      this.noNewsElement?.classList.remove('d-none')
    }
  }

  private arrangeCards(): void {
    const allNewsCol = document.querySelectorAll('.news-col')
    let sumWidth = 0
    let lastNormal: Node | null = null

    allNewsCol.forEach((news, index) => {
      setTimeout(() => {
        news.classList.add('show')
      }, 50 * index + 200)

      const width = 1 + (news.classList.contains('news-col-featured') ? 1 : 0)
      sumWidth += width

      if (sumWidth > 4 && lastNormal) {
        news.parentNode?.insertBefore(lastNormal, news.nextElementSibling)
      }

      if (width === 1) lastNormal = news
      sumWidth %= 4
    })
  }

  private async resetFilters(): Promise<void> {
    document.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
      if (checkbox instanceof HTMLInputElement) {
        checkbox.checked = false
      }
    })
    document.querySelectorAll('.dropdown-toggle').forEach((toggle) => {
      const category = toggle
        .closest('.multiselect-dropdown')
        ?.getAttribute('data-filter-category')
      if (category && toggle instanceof HTMLElement) {
        toggle.innerText = `${
          category.charAt(0).toUpperCase() + category.slice(1)
        } ▼`
      }
    })

    this.updateURL([])
    document.querySelectorAll('.multiselect-dropdown').forEach((dropdown) => {
      ;(dropdown as HTMLElement).style.opacity = '1'
    })
  }

  public async checkUrlParamsAndApplyFilters(): Promise<boolean> {
    const urlParams = new URLSearchParams(window.location.search)
    const filterParam = urlParams.get('filter')

    if (filterParam) {
      const filterValues = filterParam.split(',')
      let hasValidFilters = false

      // Check boxes first
      filterValues.forEach((value) => {
        const checkbox = document.querySelector(
          `input[data-url-parameter="${value}"]`
        )
        if (checkbox instanceof HTMLInputElement) {
          checkbox.checked = true
          checkbox.dispatchEvent(new Event('change'))
          hasValidFilters = true
        }
      })

      if (hasValidFilters) {
        // Apply filters if we have valid ones
        await this.applyFilters()
        return true
      }
    }

    return false
  }
}

// Initialize on DOM load
if (document.querySelector('.news-masonry')) {
  document.addEventListener('DOMContentLoaded', () => {
    const newsFilter = new NewsFilter()
  })
}
